import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { X } from "@phosphor-icons/react";
import { Formik } from "formik";

import type { Target } from "api/types/aba";
import useCreateTarget from "hooks/api/targets/useCreateTarget";
import useUpdateTarget from "hooks/api/targets/useUpdateTarget";

import ClinicalPhasePill from "../../shared/ClinicalPhasePill";
import ConfirmDialog from "../../shared/ConfirmDialog";
import StatusPill from "../../shared/StatusPill";
import TargetActionBar from "./TargetActionBar";
import TargetFormSection1 from "./sections/TargetFormSection1";
import TargetFormSection2 from "./sections/TargetFormSection2";
import TargetFormSection3 from "./sections/TargetFormSection3";
import TargetFormSection4 from "./sections/TargetFormSection4";
import TargetPreviewPane from "./preview/TargetPreviewPane";
import type { TargetFormValues } from "./TargetBuilderPanel.types";
import {
  TARGET_FORM_INITIAL_VALUES,
  formValuesToTargetPayload,
  targetToFormValues,
} from "./TargetBuilderPanel.types";
import { buildTargetValidationSchema } from "./validationSchema";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TargetBuilderPanelProps {
  /** "create" opens a blank form; "edit" pre-populates from `target`. */
  mode: "create" | "edit";
  /** Parent goal — required. Used for the create POST endpoint and the FCR dropdown source. */
  goalId: number;
  /** Parent goal's behavior subject (client / caregiver) — drives prompt-hierarchy visibility. */
  goalBehaviorSubject?: "client" | "caregiver";
  /** Target to pre-populate in edit mode. */
  target?: Target;
  /** Active behavior-reduction goals for the FCR dropdown (Section 1). */
  replacementGoalOptions?: Array<{ id: number; title: string }>;
  /** Active replacement-behavior targets for BR linkage in teaching procedure. */
  replacementBehaviorTargetOptions?: Array<{ id: number; title: string }>;
  /** Called after a successful save or when the user discards changes. */
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// TargetBuilderPanel
// ---------------------------------------------------------------------------

/**
 * Full-page builder for creating or editing a target.
 *
 * Layout:
 *   - Left column: form (Sections 1–4) inside a scrollable container.
 *   - Right column: sticky preview pane (hidden below `lg`).
 *   - Sticky footer: TargetActionBar with Discard / Create-or-Save buttons.
 *
 * Owns its own Formik state and React Query mutations. A discard-confirmation
 * dialog appears when the user clicks "Discard Changes" or attempts to close
 * with unsaved edits — mirroring the GoalBuilderPanel behaviour.
 */
const TargetBuilderPanel: React.FC<TargetBuilderPanelProps> = ({
  mode,
  goalId,
  goalBehaviorSubject = "client",
  target,
  replacementGoalOptions,
  replacementBehaviorTargetOptions,
  onClose,
}) => {
  const isEdit = mode === "edit";
  const [discardOpen, setDiscardOpen] = useState(false);

  const { createTargetAction, isCreatingTarget } = useCreateTarget({ onSuccess: onClose });
  const { updateTargetAction, isUpdatingTarget } = useUpdateTarget({ onSuccess: onClose });
  const isSubmitting = isCreatingTarget || isUpdatingTarget;

  const initialValues: TargetFormValues =
    isEdit && target ? targetToFormValues(target) : TARGET_FORM_INITIAL_VALUES;

  // Replacement-goal link is only enforceable when there are goals to link to.
  const hasReplacementGoalOptions = (replacementGoalOptions?.length ?? 0) > 0;
  const validationSchema = useMemo(
    () =>
      buildTargetValidationSchema({
        hasReplacementGoalOptions,
        goalBehaviorSubject,
      }),
    [hasReplacementGoalOptions, goalBehaviorSubject]
  );

  const handleSubmit = (values: TargetFormValues) => {
    const payload = formValuesToTargetPayload(values);
    if (isEdit && target) {
      // The backend validates phase as a *transition* Only include `phase`
      // in the PUT payload when the user actually changed it.
      if (values.phase === target.phase) {
        delete payload.phase;
      }
      updateTargetAction({ targetId: target.id, goalId, targetData: payload });
    } else {
      createTargetAction({ goalId, targetData: payload });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnMount
    >
      {(formik) => {
        const { values } = formik;
        const attemptClose = () => {
          if (formik.dirty) setDiscardOpen(true);
          else onClose();
        };

        const handleDiscardConfirm = () => {
          setDiscardOpen(false);
          formik.resetForm();
          onClose();
        };

        return (
          <Box
            component="section"
            aria-label={isEdit ? "Edit target" : "Create target"}
            sx={{
              minHeight: "100%",
              p: { xs: 4, md: 6 },
            }}
          >
            {/* Header — live title + phase/status pills */}
            <Stack direction="row" alignItems="center" gap={2} mb={4}>
              <Typography
                component="h1"
                sx={{
                  fontSize: "20px",
                  fontWeight: 500,
                  lineHeight: 1.6,
                  letterSpacing: "0.15px",
                  color: "text.primary",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                }}
              >
                {values.title.trim() || "New Target"}
              </Typography>
              <ClinicalPhasePill phase={values.phase} />
              <StatusPill status={values.status} />
              <Box sx={{ flex: 1 }} />
              <IconButton
                size="small"
                aria-label="Close builder"
                onClick={attemptClose}
                disabled={isSubmitting}
              >
                <X size={20} />
              </IconButton>
            </Stack>

            {/* Two-column layout */}
            <Stack direction="row" gap={6} alignItems="flex-start">
              {/* Form column */}
              <Stack gap={4} flex={1} minWidth={0}>
                <TargetFormSection1 replacementGoalOptions={replacementGoalOptions} />
                <TargetFormSection2
                  behaviorSubject={goalBehaviorSubject}
                  replacementBehaviorTargetOptions={replacementBehaviorTargetOptions}
                />
                <TargetFormSection3 />
                <TargetFormSection4 />

                <TargetActionBar
                  isEdit={isEdit}
                  isSubmitting={isSubmitting}
                  canSubmit={formik.isValid && (formik.dirty || !isEdit)}
                  onDiscard={attemptClose}
                  onSubmit={() => formik.submitForm()}
                />
              </Stack>

              <TargetPreviewPane behaviorSubject={goalBehaviorSubject} />
            </Stack>

            {/* Navigation guard while dirty (in-app route change / browser unload) */}
            <DirtyGuard dirty={formik.dirty && !isSubmitting} />

            <ConfirmDialog
              open={discardOpen}
              title="Discard unsaved changes?"
              confirmLabel="Discard"
              cancelLabel="Keep Editing"
              onClose={() => setDiscardOpen(false)}
              onCancel={() => setDiscardOpen(false)}
              onConfirm={handleDiscardConfirm}
            >
              <Typography variant="body1" color="text.secondary" sx={{ py: 3 }}>
                You have unsaved changes that will be lost if you leave this builder.
              </Typography>
            </ConfirmDialog>
          </Box>
        );
      }}
    </Formik>
  );
};

// ---------------------------------------------------------------------------
// DirtyGuard — block browser unload while the form has unsaved changes
// ---------------------------------------------------------------------------

const DirtyGuard: React.FC<{ dirty: boolean }> = ({ dirty }) => {
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);
  return null;
};

export default TargetBuilderPanel;
